import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { X, CheckCircle, AlertTriangle, FileText, Shield, Users, CreditCard, BarChart3 } from 'lucide-react';

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionCard({ title, icon, accent = 'gray', children }) {
  const borders = {
    amber: 'border-l-amber-400',
    blue: 'border-l-blue-400',
    teal: 'border-l-teal-500',
    green: 'border-l-green-500',
    purple: 'border-l-purple-400',
    red: 'border-l-red-400',
    gray: 'border-l-gray-300',
  };
  return (
    <div className={`bg-white rounded-xl border border-gray-200 border-l-4 ${borders[accent]} shadow-sm`}>
      <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
        {icon}
        <h3 className="text-sm font-semibold text-gray-700 tracking-wide">{title}</h3>
      </div>
      <div className="px-5 py-4 space-y-3">
        {children}
      </div>
    </div>
  );
}

function CheckRow({ checked, onChange, label, required, disabled, comment, onCommentChange, commentPlaceholder = 'Add comments…' }) {
  return (
    <div className={`rounded-lg border px-4 py-3 transition-colors ${checked ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-gray-50'}`}>
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          disabled={disabled}
          className="w-4 h-4 rounded accent-green-600 flex-shrink-0"
        />
        <span className="text-sm font-medium text-gray-800 flex-1">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </span>
        {checked && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
      </label>
      {onCommentChange && (
        <textarea
          value={comment}
          onChange={e => onCommentChange(e.target.value)}
          placeholder={commentPlaceholder}
          rows={2}
          disabled={disabled}
          className="mt-2 w-full text-sm px-3 py-1.5 border border-gray-200 rounded-lg bg-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:bg-gray-100 disabled:text-gray-500"
        />
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'w-full text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:bg-gray-50 disabled:text-gray-500';

// ─── Main component ───────────────────────────────────────────────────────────

export default function VendorApprovalModal({ vendorId, vendorName, onClose, onApproved }) {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    isKnownToBusiness: false,
    isKnownComments: '',
    isTrusted: false,
    isTrustedComments: '',
    negotiatorName: '',
    negotiatorEmail: '',
    negotiatorTitle: '',
    negotiationDate: '',
    hasContract: false,
    contractStartDate: '',
    contractEndDate: '',
    contractValue: '',
    hasInsurance: false,
    insuranceExpiryDate: '',
    hasComplianceCertificates: false,
    complianceCertificatesComments: '',
    paymentTermsAgreed: false,
    paymentTermsComments: '',
    creditCheckCompleted: false,
    creditCheckResult: '',
    creditCheckDate: '',
    referencesVerified: false,
    referencesComments: '',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    businessRegistrationNumber: '',
    vatRegistrationNumber: '',
    riskAssessment: '',
    riskComments: '',
    additionalNotes: '',
    paymentFrequency: '',
    paymentDayOfMonth: '',
    renewalDate: ''
  });

  useEffect(() => { fetchChecklist(); }, [vendorId]);

  const fetchChecklist = async () => {
    try {
      const response = await apiClient.get(`/vendor-approvals/${vendorId}`);
      if (response.data) {
        setChecklist(response.data);
        const d = response.data;
        setFormData({
          isKnownToBusiness: d.IsKnownToBusiness || false,
          isKnownComments: d.IsKnownComments || '',
          isTrusted: d.IsTrusted || false,
          isTrustedComments: d.IsTrustedComments || '',
          negotiatorName: d.NegotiatorName || '',
          negotiatorEmail: d.NegotiatorEmail || '',
          negotiatorTitle: d.NegotiatorTitle || '',
          negotiationDate: formatDateForInput(d.NegotiationDate),
          hasContract: d.HasContract || false,
          contractStartDate: formatDateForInput(d.ContractStartDate),
          contractEndDate: formatDateForInput(d.ContractEndDate),
          contractValue: d.ContractValue || '',
          hasInsurance: d.HasInsurance || false,
          insuranceExpiryDate: formatDateForInput(d.InsuranceExpiryDate),
          hasComplianceCertificates: d.HasComplianceCertificates || false,
          complianceCertificatesComments: d.ComplianceCertificatesComments || '',
          paymentTermsAgreed: d.PaymentTermsAgreed || false,
          paymentTermsComments: d.PaymentTermsComments || '',
          creditCheckCompleted: d.CreditCheckCompleted || false,
          creditCheckResult: d.CreditCheckResult || '',
          creditCheckDate: formatDateForInput(d.CreditCheckDate),
          referencesVerified: d.ReferencesVerified || false,
          referencesComments: d.ReferencesComments || '',
          primaryContactName: d.PrimaryContactName || '',
          primaryContactEmail: d.PrimaryContactEmail || '',
          primaryContactPhone: d.PrimaryContactPhone || '',
          businessRegistrationNumber: d.BusinessRegistrationNumber || '',
          vatRegistrationNumber: d.VATRegistrationNumber || '',
          riskAssessment: d.RiskAssessment || '',
          riskComments: d.RiskComments || '',
          additionalNotes: d.AdditionalNotes || '',
          paymentFrequency: d.PaymentFrequency || '',
          paymentDayOfMonth: d.PaymentDayOfMonth || '',
          renewalDate: formatDateForInput(d.RenewalDate),
        });
      }
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/vendor-approvals/${vendorId}`, formData);
      
      // Upload contract if selected
      if (contractFile) {
        const contractFormData = new FormData();
        contractFormData.append('contract', contractFile);
        await apiClient.post(`/vendor-approvals/${vendorId}/upload-contract`, contractFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      alert('Checklist saved successfully');
      fetchChecklist();
    } catch (error) {
      console.error('Failed to save checklist:', error);
      alert('Failed to save checklist');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    const requiredFields = [
      formData.isKnownToBusiness,
      formData.isTrusted,
      formData.negotiatorName,
      formData.negotiatorEmail,
      formData.paymentTermsAgreed
    ];

    if (!requiredFields.every(Boolean)) {
      alert('Please complete all required fields before approving');
      return;
    }

    if (!confirm(`Are you sure you want to approve vendor "${vendorName}"?`)) {
      return;
    }

    setSaving(true);
    try {
      // Save checklist first
      await apiClient.post(`/vendor-approvals/${vendorId}`, formData);
      
      // Then approve
      await apiClient.post(`/vendor-approvals/${vendorId}/approve`);
      
      alert('Vendor approved successfully');
      onApproved();
      onClose();
    } catch (error) {
      console.error('Failed to approve vendor:', error);
      alert('Failed to approve vendor');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 shadow-xl text-sm text-gray-500">Loading checklist…</div>
      </div>
    );
  }

  const isApproved = checklist?.ApprovalStatus === 'Approved';
  const readOnly = isApproved && !isEditing;

  // Required-fields progress
  const requiredItems = [
    formData.isKnownToBusiness,
    formData.isTrusted,
    !!formData.negotiatorName,
    !!formData.negotiatorEmail,
    formData.paymentTermsAgreed,
  ];
  const completedRequired = requiredItems.filter(Boolean).length;
  const progressPct = Math.round((completedRequired / requiredItems.length) * 100);

  const set = (field) => (val) => setFormData(prev => ({ ...prev, [field]: val }));
  const setE = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-50 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">

        {/* ── Header ── */}
        <div className="flex justify-between items-start px-6 py-4 bg-white border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Vendor Approval Checklist</h2>
            <p className="text-sm text-gray-500 mt-0.5">{vendorName}</p>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
                    {isApproved && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                <CheckCircle className="w-3.5 h-3.5" />
                Approved
              </span>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── Progress bar ── */}
        {!isApproved && (
          <div className="px-6 py-2.5 bg-white border-b border-gray-100">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
              <span className="font-medium">Required items</span>
              <span className={completedRequired === requiredItems.length ? 'text-green-600 font-semibold' : ''}>
                {completedRequired}/{requiredItems.length} complete
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${completedRequired === requiredItems.length ? 'bg-green-500' : 'bg-blue-500'}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* 1 · Business Validation */}
          <SectionCard title="Business Validation" icon={<AlertTriangle className="w-4 h-4 text-amber-500" />} accent="amber">
            <CheckRow
              checked={formData.isKnownToBusiness} onChange={set('isKnownToBusiness')}
              label="Is this vendor known to the business?" required disabled={readOnly}
              comment={formData.isKnownComments} onCommentChange={set('isKnownComments')}
            />
            <CheckRow
              checked={formData.isTrusted} onChange={set('isTrusted')}
              label="Is this vendor trusted?" required disabled={readOnly}
              comment={formData.isTrustedComments} onCommentChange={set('isTrustedComments')}
            />
          </SectionCard>

          {/* 2 · Negotiation Details */}
          <SectionCard title="Negotiation Details" icon={<Users className="w-4 h-4 text-blue-500" />} accent="blue">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Negotiator Name" required>
                <input type="text" value={formData.negotiatorName} onChange={setE('negotiatorName')} disabled={readOnly} className={inputCls} />
              </Field>
              <Field label="Negotiator Email" required>
                <input type="email" value={formData.negotiatorEmail} onChange={setE('negotiatorEmail')} disabled={readOnly} className={inputCls} />
              </Field>
              <Field label="Negotiator Title">
                <input type="text" value={formData.negotiatorTitle} onChange={setE('negotiatorTitle')} disabled={readOnly} className={inputCls} />
              </Field>
              <Field label="Negotiation Date">
                <input type="date" value={formData.negotiationDate} onChange={setE('negotiationDate')} disabled={readOnly} className={inputCls} />
              </Field>
            </div>
          </SectionCard>

          {/* 3 · Contract Details */}
          <SectionCard title="Contract Details" icon={<FileText className="w-4 h-4 text-teal-500" />} accent="teal">
            <CheckRow
              checked={formData.hasContract} onChange={set('hasContract')}
              label="Contract Available" disabled={readOnly}
            />
            {formData.hasContract && (
              <div className="grid grid-cols-2 gap-3 pt-1">
                <Field label="Contract Start Date">
                  <input type="date" value={formData.contractStartDate} onChange={setE('contractStartDate')} disabled={readOnly} className={inputCls} />
                </Field>
                <Field label="Contract End Date">
                  <input type="date" value={formData.contractEndDate} onChange={setE('contractEndDate')} disabled={readOnly} className={inputCls} />
                </Field>
                <Field label="Contract Value">
                  <input type="number" value={formData.contractValue} onChange={setE('contractValue')} disabled={readOnly} className={inputCls} />
                </Field>
                {!readOnly && (
                  <Field label="Upload Contract Document">
                    <input type="file" accept=".pdf,.doc,.docx" onChange={e => setContractFile(e.target.files[0])} className={inputCls} />
                  </Field>
                )}
                {checklist?.ContractDocumentUrl && (
                  <div className="col-span-2">
                    <a href={checklist.ContractDocumentUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline">
                      <FileText className="w-4 h-4" /> View Uploaded Contract
                    </a>
                  </div>
                )}
              </div>
            )}
            <CheckRow
              checked={formData.hasInsurance} onChange={set('hasInsurance')}
              label="Insurance in Place" disabled={readOnly}
            />
            {formData.hasInsurance && (
              <div className="pt-1 w-56">
                <Field label="Insurance Expiry Date">
                  <input type="date" value={formData.insuranceExpiryDate} onChange={setE('insuranceExpiryDate')} disabled={readOnly} className={inputCls} />
                </Field>
              </div>
            )}
          </SectionCard>

          {/* 4 · Payment & Renewal */}
          <SectionCard title="Payment & Renewal" icon={<CreditCard className="w-4 h-4 text-green-500" />} accent="green">
            <CheckRow
              checked={formData.paymentTermsAgreed} onChange={set('paymentTermsAgreed')}
              label="Payment Terms Agreed" required disabled={readOnly}
              comment={formData.paymentTermsComments} onCommentChange={set('paymentTermsComments')}
            />
            <div className="grid grid-cols-3 gap-3">
              <Field label="Payment Frequency">
                <select value={formData.paymentFrequency} onChange={setE('paymentFrequency')} disabled={readOnly} className={inputCls}>
                  <option value="">Not specified</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Annually">Annually</option>
                </select>
              </Field>
              <Field label="Payment Day of Month">
                <input type="number" min="1" max="31" placeholder="1–31" value={formData.paymentDayOfMonth} onChange={setE('paymentDayOfMonth')} disabled={readOnly} className={inputCls} />
              </Field>
              <Field label="Renewal Date">
                <input type="date" value={formData.renewalDate} onChange={setE('renewalDate')} disabled={readOnly} className={inputCls} />
              </Field>
            </div>
          </SectionCard>

          {/* 5 · Compliance & Due Diligence */}
          <SectionCard title="Compliance & Due Diligence" icon={<Shield className="w-4 h-4 text-purple-500" />} accent="purple">
            <CheckRow
              checked={formData.creditCheckCompleted} onChange={set('creditCheckCompleted')}
              label="Credit Check Completed" disabled={readOnly}
            />
            {formData.creditCheckCompleted && (
              <div className="grid grid-cols-2 gap-3 pt-1">
                <Field label="Result">
                  <input type="text" value={formData.creditCheckResult} onChange={setE('creditCheckResult')} placeholder="Good, Fair, Poor…" disabled={readOnly} className={inputCls} />
                </Field>
                <Field label="Date Checked">
                  <input type="date" value={formData.creditCheckDate} onChange={setE('creditCheckDate')} disabled={readOnly} className={inputCls} />
                </Field>
              </div>
            )}
            <CheckRow
              checked={formData.hasComplianceCertificates} onChange={set('hasComplianceCertificates')}
              label="Compliance Certificates Obtained" disabled={readOnly}
              comment={formData.complianceCertificatesComments} onCommentChange={set('complianceCertificatesComments')}
            />
            <CheckRow
              checked={formData.referencesVerified} onChange={set('referencesVerified')}
              label="References Verified" disabled={readOnly}
              comment={formData.referencesComments} onCommentChange={set('referencesComments')}
            />
          </SectionCard>

          {/* 6 · Risk Assessment */}
          <SectionCard title="Risk Assessment" icon={<BarChart3 className="w-4 h-4 text-red-400" />} accent="red">
            <div className="grid grid-cols-3 gap-2">
              {['Low', 'Medium', 'High'].map(level => {
                const colours = {
                  Low: 'border-green-400 bg-green-50 text-green-700',
                  Medium: 'border-amber-400 bg-amber-50 text-amber-700',
                  High: 'border-red-400 bg-red-50 text-red-700',
                };
                const selected = formData.riskAssessment === level;
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => !readOnly && setFormData(p => ({ ...p, riskAssessment: level }))}
                    disabled={readOnly}
                    className={`py-2 text-sm font-medium rounded-lg border-2 transition-all ${selected ? colours[level] : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300'} disabled:cursor-default`}
                  >
                    {level} Risk
                  </button>
                );
              })}
            </div>
            <Field label="Comments">
              <textarea value={formData.riskComments} onChange={setE('riskComments')} rows={2} disabled={readOnly} className={`${inputCls} resize-none`} />
            </Field>
          </SectionCard>

          {/* 7 · Additional Notes */}
          <SectionCard title="Additional Notes" icon={<FileText className="w-4 h-4 text-gray-400" />} accent="gray">
            <textarea
              value={formData.additionalNotes}
              onChange={setE('additionalNotes')}
              rows={3}
              disabled={readOnly}
              placeholder="Any additional information about this vendor…"
              className={`${inputCls} resize-none`}
            />
          </SectionCard>

        </div>

        {/* ── Footer ── */}
        {!isApproved ? (
          <div className="border-t border-gray-200 px-6 py-4 bg-white flex items-center justify-between gap-3">
            <p className="text-xs text-gray-400 hidden sm:block">
              Fields marked <span className="text-red-500 font-semibold">*</span> are required to approve
            </p>
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {saving ? 'Saving…' : 'Save Progress'}
              </button>
              <button
                onClick={handleApprove}
                disabled={saving || completedRequired < requiredItems.length}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                {saving ? 'Approving…' : 'Approve Vendor'}
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-gray-200 px-6 py-4 bg-white flex items-center justify-between gap-3">
            {isEditing ? (
              <span className="text-xs text-amber-600 font-medium">Editing approved checklist — changes will be saved</span>
            ) : (
              <span className="text-xs text-gray-400">Vendor is approved. You can still edit details or upload documents.</span>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Close
              </button>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Edit Checklist
                </button>
              ) : (
                <button
                  onClick={async () => { await handleSave(); setIsEditing(false); }}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
